import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../components/AdminLayout';
import { Plus, Save, Trash2, Edit } from 'lucide-react';
import { apiService } from '../services/api';

const Container = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: none;
  margin: 0;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #0f172a;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 1rem;
  height: fit-content;
`;

const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SectionButton = styled.button<{ $active?: boolean }>`
  text-align: left;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 1px solid ${props => (props.$active ? '#00d4aa' : '#e2e8f0')};
  background: ${props => (props.$active ? '#ecfeff' : '#fff')};
  font-weight: 600;
  color: #0f172a;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 1.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: #475569;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  min-height: 120px;
  resize: vertical;
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.9rem;
  border-radius: 8px;
  font-weight: 600;
  border: 1px solid transparent;
  background: ${props => (props.$variant === 'primary' ? '#00d4aa' : props.$variant === 'danger' ? '#fee2e2' : '#f8fafc')};
  color: ${props => (props.$variant === 'danger' ? '#b91c1c' : '#0f172a')};
`;

const ItemsList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const ItemRow = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Badge = styled.span`
  font-size: 0.75rem;
  color: #64748b;
`;

const MetaNote = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
`;

type Section = {
  id: number;
  key: string;
  title?: string;
  subtitle?: string;
  description?: string;
  meta?: any;
  sort_order?: number;
  is_active?: boolean;
  items_count?: number;
};

type Item = {
  id: number;
  title?: string;
  subtitle?: string;
  description?: string;
  icon?: string;
  image_path?: string;
  link_url?: string;
  meta?: any;
  sort_order?: number;
  is_active?: boolean;
};

const emptySection: Partial<Section> = {
  key: '',
  title: '',
  subtitle: '',
  description: '',
  meta: {},
  sort_order: 0,
  is_active: true,
};

const emptyItem: Partial<Item> = {
  title: '',
  subtitle: '',
  description: '',
  icon: '',
  image_path: '',
  link_url: '',
  meta: {},
  sort_order: 0,
  is_active: true,
};

export const AdminSiteContentPage: React.FC = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [activeSection, setActiveSection] = useState<Section | null>(null);
  const [sectionForm, setSectionForm] = useState<Partial<Section>>(emptySection);
  const [items, setItems] = useState<Item[]>([]);
  const [itemForm, setItemForm] = useState<Partial<Item>>(emptyItem);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const loadSections = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.axiosInstance.get('/admin/site-sections');
      if (response.data?.success) {
        setSections(response.data.data || []);
        if (!activeSection && response.data.data?.length) {
          setActiveSection(response.data.data[0]);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [activeSection]);

  const loadItems = async (sectionId: number) => {
    const response = await apiService.axiosInstance.get(`/admin/site-sections/${sectionId}/items`);
    if (response.data?.success) {
      setItems(response.data.data || []);
    }
  };

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  useEffect(() => {
    if (activeSection) {
      setSectionForm({
        ...activeSection,
        meta: activeSection.meta || {},
      });
      loadItems(activeSection.id);
    }
  }, [activeSection]);

  const sectionKey = activeSection?.key || '';

  const sectionMeta = useMemo(() => {
    return (sectionForm.meta as any) || {};
  }, [sectionForm.meta]);

  const updateSectionMeta = (key: string, value: any) => {
    setSectionForm(prev => ({
      ...prev,
      meta: {
        ...(prev.meta || {}),
        [key]: value,
      }
    }));
  };

  const updateSectionMetaArray = (key: string, index: number, field: string, value: any) => {
    const list = Array.isArray(sectionMeta[key]) ? [...sectionMeta[key]] : [];
    list[index] = { ...(list[index] || {}), [field]: value };
    updateSectionMeta(key, list);
  };

  const addSectionMetaItem = (key: string, defaultItem: any) => {
    const list = Array.isArray(sectionMeta[key]) ? [...sectionMeta[key]] : [];
    list.push(defaultItem);
    updateSectionMeta(key, list);
  };

  const removeSectionMetaItem = (key: string, index: number) => {
    const list = Array.isArray(sectionMeta[key]) ? [...sectionMeta[key]] : [];
    list.splice(index, 1);
    updateSectionMeta(key, list);
  };

  const handleSectionUpload = async (metaKey: string, file?: File | null) => {
    if (!activeSection || !file) return;
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('key', metaKey);
      formData.append('file', file);
      const response = await apiService.axiosInstance.post(`/admin/site-sections/${activeSection.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data?.success && response.data.data?.url) {
        updateSectionMeta(metaKey, response.data.data.url);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleItemUpload = async (file?: File | null) => {
    if (!itemForm.id || !file) return;
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiService.axiosInstance.post(`/admin/site-items/${itemForm.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data?.success && response.data.data?.path) {
        setItemForm(prev => ({ ...prev, image_path: response.data.data.path }));
        if (activeSection) {
          await loadItems(activeSection.id);
        }
      }
    } finally {
      setIsUploading(false);
    }
  };

  const updateItemMeta = (key: string, value: any) => {
    setItemForm(prev => ({
      ...prev,
      meta: {
        ...(prev.meta || {}),
        [key]: value,
      }
    }));
  };

  const handleSaveSection = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        title: sectionForm.title || null,
        subtitle: sectionForm.subtitle || null,
        description: sectionForm.description || null,
        meta: sectionForm.meta || {},
        sort_order: Number(sectionForm.sort_order || 0),
        is_active: sectionForm.is_active ?? true,
      };
      if (activeSection) {
        await apiService.axiosInstance.put(`/admin/site-sections/${activeSection.id}`, payload);
      } else {
        await apiService.axiosInstance.post('/admin/site-sections', {
          ...payload,
          key: sectionForm.key,
        });
      }
      await loadSections();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSection = async () => {
    if (!activeSection) return;
    if (!window.confirm('Deseja excluir esta secao?')) return;
    await apiService.axiosInstance.delete(`/admin/site-sections/${activeSection.id}`);
    setActiveSection(null);
    setItems([]);
    await loadSections();
  };

  const handleSaveItem = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeSection) return;
    setIsSaving(true);
    try {
      const payload = {
        title: itemForm.title || null,
        subtitle: itemForm.subtitle || null,
        description: itemForm.description || null,
        icon: itemForm.icon || null,
        image_path: itemForm.image_path || null,
        link_url: itemForm.link_url || null,
        meta: itemForm.meta || {},
        sort_order: Number(itemForm.sort_order || 0),
        is_active: itemForm.is_active ?? true,
      };
      if (itemForm.id) {
        await apiService.axiosInstance.put(`/admin/site-items/${itemForm.id}`, payload);
      } else {
        await apiService.axiosInstance.post(`/admin/site-sections/${activeSection.id}/items`, payload);
      }
      setItemForm(emptyItem);
      await loadItems(activeSection.id);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!window.confirm('Deseja excluir este item?')) return;
    await apiService.axiosInstance.delete(`/admin/site-items/${itemId}`);
    if (activeSection) {
      await loadItems(activeSection.id);
    }
  };

  return (
    <AdminLayout>
      <Container>
        <Header>
          <Title>Conteudo do Site</Title>
          <Button $variant="primary" onClick={() => {
            setActiveSection(null);
            setSectionForm(emptySection);
            setItems([]);
          }}>
            <Plus size={16} />
            Nova Secao
          </Button>
        </Header>

        <Layout>
          <Sidebar>
            <SectionList>
              {sections.map((section) => (
                <SectionButton
                  key={section.id}
                  $active={activeSection?.id === section.id}
                  onClick={() => setActiveSection(section)}
                >
                  {section.title || section.key}
                </SectionButton>
              ))}
            </SectionList>
          </Sidebar>

          <ContentCard>
            {loading ? (
              <div>Carregando...</div>
            ) : (
              <>
                <SectionHeader>
                  <div>
                    <strong>{activeSection ? 'Editar Secao' : 'Nova Secao'}</strong>
                  </div>
                  {activeSection && (
                    <Button $variant="danger" onClick={handleDeleteSection}>
                      <Trash2 size={16} />
                      Excluir
                    </Button>
                  )}
                </SectionHeader>

                <Form onSubmit={handleSaveSection}>
                  {!activeSection && (
                    <div>
                      <Label>Chave da Secao</Label>
                      <Input
                        value={sectionForm.key || ''}
                        onChange={(e) => setSectionForm(prev => ({ ...prev, key: e.target.value }))}
                        placeholder="ex: benefits, hero"
                        required
                      />
                    </div>
                  )}
                  <Row>
                    <div>
                      <Label>Titulo</Label>
                      <Input value={sectionForm.title || ''} onChange={(e) => setSectionForm(prev => ({ ...prev, title: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Subtitulo</Label>
                      <Input value={sectionForm.subtitle || ''} onChange={(e) => setSectionForm(prev => ({ ...prev, subtitle: e.target.value }))} />
                    </div>
                  </Row>
                  <div>
                    <Label>Descricao</Label>
                    <Textarea value={sectionForm.description || ''} onChange={(e) => setSectionForm(prev => ({ ...prev, description: e.target.value }))} />
                  </div>

                  {sectionKey === 'header' && (
                    <>
                      <SectionHeader>
                        <strong>Logo e Navegacao</strong>
                      </SectionHeader>
                      {sectionMeta.logo_url && (
                        <img src={sectionMeta.logo_url} alt="Logo" style={{ height: '48px', borderRadius: '6px' }} />
                      )}
                      <Input type="file" accept="image/*" onChange={(e) => handleSectionUpload('logo_url', e.target.files?.[0])} disabled={!activeSection || isUploading} />
                      <SectionHeader>
                        <strong>Links do menu</strong>
                      </SectionHeader>
                      {(sectionMeta.nav || []).map((item: any, index: number) => (
                        <Row key={`nav-${index}`}>
                          <div>
                            <Label>Label</Label>
                            <Input value={item.label || ''} onChange={(e) => updateSectionMetaArray('nav', index, 'label', e.target.value)} />
                          </div>
                          <div>
                            <Label>Href</Label>
                            <Input value={item.href || ''} onChange={(e) => updateSectionMetaArray('nav', index, 'href', e.target.value)} />
                          </div>
                          <Button type="button" $variant="danger" onClick={() => removeSectionMetaItem('nav', index)}>
                            <Trash2 size={14} />
                            Remover
                          </Button>
                        </Row>
                      ))}
                      <Button type="button" onClick={() => addSectionMetaItem('nav', { label: '', href: '' })}>
                        <Plus size={14} />
                        Adicionar Link
                      </Button>
                      <SectionHeader>
                        <strong>Botoes</strong>
                      </SectionHeader>
                      {(sectionMeta.buttons || []).map((item: any, index: number) => (
                        <Row key={`button-${index}`}>
                          <div>
                            <Label>Label</Label>
                            <Input value={item.label || ''} onChange={(e) => updateSectionMetaArray('buttons', index, 'label', e.target.value)} />
                          </div>
                          <div>
                            <Label>Acao</Label>
                            <Input value={item.action || ''} onChange={(e) => updateSectionMetaArray('buttons', index, 'action', e.target.value)} />
                          </div>
                          <Button type="button" $variant="danger" onClick={() => removeSectionMetaItem('buttons', index)}>
                            <Trash2 size={14} />
                            Remover
                          </Button>
                        </Row>
                      ))}
                      <Button type="button" onClick={() => addSectionMetaItem('buttons', { label: '', action: 'register' })}>
                        <Plus size={14} />
                        Adicionar Botao
                      </Button>
                      <MetaNote>Use acoes: login, register ou informe href.</MetaNote>
                    </>
                  )}

                  {sectionKey === 'hero' && (
                    <>
                      <Row>
                        <div>
                          <Label>Badge</Label>
                          <Input value={sectionMeta.badge || ''} onChange={(e) => updateSectionMeta('badge', e.target.value)} />
                        </div>
                        <div>
                          <Label>Titulo</Label>
                          <Input value={sectionMeta.title || ''} onChange={(e) => updateSectionMeta('title', e.target.value)} />
                        </div>
                      </Row>
                      <div>
                        <Label>Subtitulo</Label>
                        <Textarea value={sectionMeta.subtitle || ''} onChange={(e) => updateSectionMeta('subtitle', e.target.value)} />
                      </div>
                      <Row>
                        <div>
                          <Label>CTA Primario</Label>
                          <Input value={sectionMeta.cta_primary || ''} onChange={(e) => updateSectionMeta('cta_primary', e.target.value)} />
                        </div>
                        <div>
                          <Label>CTA Secundario</Label>
                          <Input value={sectionMeta.cta_secondary || ''} onChange={(e) => updateSectionMeta('cta_secondary', e.target.value)} />
                        </div>
                      </Row>
                      <Row>
                        <div>
                          <Label>Mockup URL</Label>
                          <Input value={sectionMeta.mockup_url || ''} onChange={(e) => updateSectionMeta('mockup_url', e.target.value)} />
                        </div>
                        <div>
                          <Label>Mockup Imagem</Label>
                          {sectionMeta.mockup_image && (
                            <img src={sectionMeta.mockup_image} alt="Mockup" style={{ height: '72px', borderRadius: '8px', display: 'block', marginBottom: '0.5rem' }} />
                          )}
                          <Input type="file" accept="image/*" onChange={(e) => handleSectionUpload('mockup_image', e.target.files?.[0])} disabled={!activeSection || isUploading} />
                        </div>
                      </Row>
                      <SectionHeader>
                        <strong>Provas sociais</strong>
                      </SectionHeader>
                      {(sectionMeta.proof || []).map((item: any, index: number) => (
                        <Row key={`proof-${index}`}>
                          <div>
                            <Label>Valor</Label>
                            <Input value={item.value || ''} onChange={(e) => updateSectionMetaArray('proof', index, 'value', e.target.value)} />
                          </div>
                          <div>
                            <Label>Label</Label>
                            <Input value={item.label || ''} onChange={(e) => updateSectionMetaArray('proof', index, 'label', e.target.value)} />
                          </div>
                          <Button type="button" $variant="danger" onClick={() => removeSectionMetaItem('proof', index)}>
                            <Trash2 size={14} />
                            Remover
                          </Button>
                        </Row>
                      ))}
                      <Button type="button" onClick={() => addSectionMetaItem('proof', { value: '', label: '' })}>
                        <Plus size={14} />
                        Adicionar Prova
                      </Button>
                      <SectionHeader>
                        <strong>Badges flutuantes</strong>
                      </SectionHeader>
                      {(sectionMeta.floating_badges || []).map((item: any, index: number) => (
                        <Row key={`badge-${index}`}>
                          <div>
                            <Label>Titulo</Label>
                            <Input value={item.title || ''} onChange={(e) => updateSectionMetaArray('floating_badges', index, 'title', e.target.value)} />
                          </div>
                          <div>
                            <Label>Subtitulo</Label>
                            <Input value={item.subtitle || ''} onChange={(e) => updateSectionMetaArray('floating_badges', index, 'subtitle', e.target.value)} />
                          </div>
                          <div>
                            <Label>Icone</Label>
                            <Input value={item.icon || ''} onChange={(e) => updateSectionMetaArray('floating_badges', index, 'icon', e.target.value)} />
                          </div>
                          <Button type="button" $variant="danger" onClick={() => removeSectionMetaItem('floating_badges', index)}>
                            <Trash2 size={14} />
                            Remover
                          </Button>
                        </Row>
                      ))}
                      <Button type="button" onClick={() => addSectionMetaItem('floating_badges', { title: '', subtitle: '', icon: '' })}>
                        <Plus size={14} />
                        Adicionar Badge
                      </Button>
                    </>
                  )}

                  {['features', 'benefits', 'reviews_summary', 'faq'].includes(sectionKey) && (
                    <Row>
                      <div>
                        <Label>Destaque</Label>
                        <Input value={sectionMeta.highlight || ''} onChange={(e) => updateSectionMeta('highlight', e.target.value)} />
                      </div>
                    </Row>
                  )}

                  {sectionKey === 'reviews_summary' && (
                    <>
                      <Row>
                        <div>
                          <Label>Nota</Label>
                          <Input type="number" step="0.1" value={sectionMeta.score || 0} onChange={(e) => updateSectionMeta('score', Number(e.target.value))} />
                        </div>
                        <div>
                          <Label>Quantidade de avaliacoes</Label>
                          <Input type="number" value={sectionMeta.count || 0} onChange={(e) => updateSectionMeta('count', Number(e.target.value))} />
                        </div>
                      </Row>
                      <SectionHeader>
                        <strong>Distribuicao</strong>
                      </SectionHeader>
                      {(sectionMeta.distribution || []).map((item: any, index: number) => (
                        <Row key={`dist-${index}`}>
                          <div>
                            <Label>Estrelas</Label>
                            <Input type="number" value={item.stars || 0} onChange={(e) => updateSectionMetaArray('distribution', index, 'stars', Number(e.target.value))} />
                          </div>
                          <div>
                            <Label>Percentual</Label>
                            <Input type="number" value={item.percent || 0} onChange={(e) => updateSectionMetaArray('distribution', index, 'percent', Number(e.target.value))} />
                          </div>
                          <Button type="button" $variant="danger" onClick={() => removeSectionMetaItem('distribution', index)}>
                            <Trash2 size={14} />
                            Remover
                          </Button>
                        </Row>
                      ))}
                      <Button type="button" onClick={() => addSectionMetaItem('distribution', { stars: 5, percent: 0 })}>
                        <Plus size={14} />
                        Adicionar Linha
                      </Button>
                    </>
                  )}

                  {sectionKey === 'trial' && (
                    <>
                      <Row>
                        <div>
                          <Label>Titulo</Label>
                          <Input value={sectionMeta.title || ''} onChange={(e) => updateSectionMeta('title', e.target.value)} />
                        </div>
                        <div>
                          <Label>Botao</Label>
                          <Input value={sectionMeta.button || ''} onChange={(e) => updateSectionMeta('button', e.target.value)} />
                        </div>
                      </Row>
                      <div>
                        <Label>Texto</Label>
                        <Textarea value={sectionMeta.text || ''} onChange={(e) => updateSectionMeta('text', e.target.value)} />
                      </div>
                    </>
                  )}

                  {sectionKey === 'cta' && (
                    <>
                      <Row>
                        <div>
                          <Label>Titulo</Label>
                          <Input value={sectionMeta.title || ''} onChange={(e) => updateSectionMeta('title', e.target.value)} />
                        </div>
                        <div>
                          <Label>Destaque</Label>
                          <Input value={sectionMeta.highlight || ''} onChange={(e) => updateSectionMeta('highlight', e.target.value)} />
                        </div>
                      </Row>
                      <Row>
                        <div>
                          <Label>Sufixo</Label>
                          <Input value={sectionMeta.suffix || ''} onChange={(e) => updateSectionMeta('suffix', e.target.value)} />
                        </div>
                        <div>
                          <Label>Botao</Label>
                          <Input value={sectionMeta.button || ''} onChange={(e) => updateSectionMeta('button', e.target.value)} />
                        </div>
                      </Row>
                      <div>
                        <Label>Subtitulo</Label>
                        <Textarea value={sectionMeta.subtitle || ''} onChange={(e) => updateSectionMeta('subtitle', e.target.value)} />
                      </div>
                    </>
                  )}

                  {sectionKey === 'footer' && (
                    <>
                      {sectionMeta.logo_url && (
                        <img src={sectionMeta.logo_url} alt="Logo" style={{ height: '40px', borderRadius: '6px' }} />
                      )}
                      <Input type="file" accept="image/*" onChange={(e) => handleSectionUpload('logo_url', e.target.files?.[0])} disabled={!activeSection || isUploading} />
                      <Row>
                        <div>
                          <Label>Texto</Label>
                          <Input value={sectionMeta.text || ''} onChange={(e) => updateSectionMeta('text', e.target.value)} />
                        </div>
                        <div>
                          <Label>Copyright</Label>
                          <Input value={sectionMeta.copyright || ''} onChange={(e) => updateSectionMeta('copyright', e.target.value)} />
                        </div>
                      </Row>
                      <div>
                        <Label>Nota de pagamento</Label>
                        <Input value={sectionMeta.payment_note || ''} onChange={(e) => updateSectionMeta('payment_note', e.target.value)} />
                      </div>
                      <SectionHeader>
                        <strong>Bandeiras e meios de pagamento</strong>
                      </SectionHeader>
                      {(sectionMeta.payment_methods || []).map((item: any, index: number) => (
                        <Row key={`payment-method-${index}`}>
                          <div>
                            <Label>Label</Label>
                            <Input value={item.label || ''} onChange={(e) => updateSectionMetaArray('payment_methods', index, 'label', e.target.value)} />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'end' }}>
                            <Button type="button" $variant="danger" onClick={() => removeSectionMetaItem('payment_methods', index)}>
                              <Trash2 size={14} />
                              Remover
                            </Button>
                          </div>
                        </Row>
                      ))}
                      <Button type="button" onClick={() => addSectionMetaItem('payment_methods', { label: '' })}>
                        <Plus size={14} />
                        Adicionar Bandeira
                      </Button>
                      <SectionHeader>
                        <strong>Selos de seguranca</strong>
                      </SectionHeader>
                      {(sectionMeta.trust_badges || []).map((item: any, index: number) => (
                        <Row key={`trust-badge-${index}`}>
                          <div>
                            <Label>Label</Label>
                            <Input value={item.label || ''} onChange={(e) => updateSectionMetaArray('trust_badges', index, 'label', e.target.value)} />
                          </div>
                          <div>
                            <Label>Subtitulo</Label>
                            <Input value={item.subtitle || ''} onChange={(e) => updateSectionMetaArray('trust_badges', index, 'subtitle', e.target.value)} />
                          </div>
                          <Button type="button" $variant="danger" onClick={() => removeSectionMetaItem('trust_badges', index)}>
                            <Trash2 size={14} />
                            Remover
                          </Button>
                        </Row>
                      ))}
                      <Button type="button" onClick={() => addSectionMetaItem('trust_badges', { label: '', subtitle: '' })}>
                        <Plus size={14} />
                        Adicionar Selo
                      </Button>
                      <SectionHeader>
                        <strong>Links do rodape</strong>
                      </SectionHeader>
                      {(sectionMeta.links || []).map((item: any, index: number) => (
                        <Row key={`footer-link-${index}`}>
                          <div>
                            <Label>Label</Label>
                            <Input value={item.label || ''} onChange={(e) => updateSectionMetaArray('links', index, 'label', e.target.value)} />
                          </div>
                          <div>
                            <Label>Href</Label>
                            <Input value={item.href || ''} onChange={(e) => updateSectionMetaArray('links', index, 'href', e.target.value)} />
                          </div>
                          <Button type="button" $variant="danger" onClick={() => removeSectionMetaItem('links', index)}>
                            <Trash2 size={14} />
                            Remover
                          </Button>
                        </Row>
                      ))}
                      <Button type="button" onClick={() => addSectionMetaItem('links', { label: '', href: '' })}>
                        <Plus size={14} />
                        Adicionar Link
                      </Button>
                    </>
                  )}

                  <Row>
                    <div>
                      <Label>Ordem</Label>
                      <Input
                        type="number"
                        value={sectionForm.sort_order ?? 0}
                        onChange={(e) => setSectionForm(prev => ({ ...prev, sort_order: Number(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <Label style={{ marginTop: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={sectionForm.is_active ?? true}
                          onChange={(e) => setSectionForm(prev => ({ ...prev, is_active: e.target.checked }))}
                        /> Secao ativa
                      </Label>
                    </div>
                  </Row>
                  <Button $variant="primary" type="submit" disabled={isSaving}>
                    <Save size={16} />
                    Salvar Secao
                  </Button>
                </Form>

                {activeSection && (
                  <>
                    <SectionHeader style={{ marginTop: '2rem' }}>
                      <strong>Itens da Secao</strong>
                    </SectionHeader>
                    <ItemsList>
                      {items.map((item) => (
                        <ItemRow key={item.id}>
                          <ItemInfo>
                            <strong>{item.title || 'Sem titulo'}</strong>
                            <Badge>Ordem: {item.sort_order ?? 0}</Badge>
                          </ItemInfo>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button onClick={() => setItemForm(item)}>
                              <Edit size={16} />
                              Editar
                            </Button>
                            <Button $variant="danger" onClick={() => handleDeleteItem(item.id)}>
                              <Trash2 size={16} />
                              Excluir
                            </Button>
                          </div>
                        </ItemRow>
                      ))}
                    </ItemsList>

                    <SectionHeader style={{ marginTop: '1.5rem' }}>
                      <strong>{itemForm.id ? 'Editar Item' : 'Novo Item'}</strong>
                    </SectionHeader>
                    <Form onSubmit={handleSaveItem}>
                      <Row>
                        <div>
                          <Label>Titulo</Label>
                          <Input value={itemForm.title || ''} onChange={(e) => setItemForm(prev => ({ ...prev, title: e.target.value }))} />
                        </div>
                        <div>
                          <Label>Subtitulo</Label>
                          <Input value={itemForm.subtitle || ''} onChange={(e) => setItemForm(prev => ({ ...prev, subtitle: e.target.value }))} />
                        </div>
                      </Row>
                      <div>
                        <Label>Descricao</Label>
                        <Textarea value={itemForm.description || ''} onChange={(e) => setItemForm(prev => ({ ...prev, description: e.target.value }))} />
                      </div>
                      {['features', 'testimonials'].includes(sectionKey) && (
                        <Row>
                          <div>
                            <Label>Icone</Label>
                            <Input value={itemForm.icon || ''} onChange={(e) => setItemForm(prev => ({ ...prev, icon: e.target.value }))} />
                          </div>
                          <div>
                            <Label>Imagem</Label>
                            <Input type="file" accept="image/*" onChange={(e) => handleItemUpload(e.target.files?.[0])} disabled={!itemForm.id || isUploading} />
                            <MetaNote>Salve o item antes de enviar imagem.</MetaNote>
                          </div>
                        </Row>
                      )}
                      <Row>
                        <div>
                          <Label>Link</Label>
                          <Input value={itemForm.link_url || ''} onChange={(e) => setItemForm(prev => ({ ...prev, link_url: e.target.value }))} />
                        </div>
                        <div>
                          <Label>Ordem</Label>
                          <Input type="number" value={itemForm.sort_order ?? 0} onChange={(e) => setItemForm(prev => ({ ...prev, sort_order: Number(e.target.value) }))} />
                        </div>
                      </Row>
                      {sectionKey === 'testimonials' && (
                        <Row>
                          <div>
                            <Label>Cor do avatar</Label>
                            <Input value={(itemForm.meta as any)?.color || ''} onChange={(e) => updateItemMeta('color', e.target.value)} />
                          </div>
                          <div>
                            <Label>Inicial</Label>
                            <Input value={(itemForm.meta as any)?.initial || ''} onChange={(e) => updateItemMeta('initial', e.target.value)} />
                          </div>
                          <div>
                            <Label>Nota</Label>
                            <Input type="number" value={(itemForm.meta as any)?.rating || 5} onChange={(e) => updateItemMeta('rating', Number(e.target.value))} />
                          </div>
                          <div>
                            <Label>Data</Label>
                            <Input value={(itemForm.meta as any)?.date || ''} onChange={(e) => updateItemMeta('date', e.target.value)} />
                          </div>
                          <div>
                            <Label>Avaliacoes</Label>
                            <Input type="number" value={(itemForm.meta as any)?.reviews || 0} onChange={(e) => updateItemMeta('reviews', Number(e.target.value))} />
                          </div>
                          <Label>
                            <input
                              type="checkbox"
                              checked={(itemForm.meta as any)?.verified ?? false}
                              onChange={(e) => updateItemMeta('verified', e.target.checked)}
                            /> Verificado
                          </Label>
                          <Label>
                            <input
                              type="checkbox"
                              checked={(itemForm.meta as any)?.guide ?? false}
                              onChange={(e) => updateItemMeta('guide', e.target.checked)}
                            /> Guia local
                          </Label>
                        </Row>
                      )}
                      {sectionKey === 'faq' && (
                        <Label>
                          <input
                            type="checkbox"
                            checked={(itemForm.meta as any)?.active ?? false}
                            onChange={(e) => updateItemMeta('active', e.target.checked)}
                          /> Destaque aberto
                        </Label>
                      )}
                      <Label>
                        <input
                          type="checkbox"
                          checked={itemForm.is_active ?? true}
                          onChange={(e) => setItemForm(prev => ({ ...prev, is_active: e.target.checked }))}
                        /> Item ativo
                      </Label>
                      <Button $variant="primary" type="submit" disabled={isSaving}>
                        <Save size={16} />
                        Salvar Item
                      </Button>
                    </Form>
                  </>
                )}
              </>
            )}
          </ContentCard>
        </Layout>
      </Container>
    </AdminLayout>
  );
};
